import {
  IThirdPartyNode,
  ThirdPartyGlobalPermission,
  ThirdPartyGlobalPermissionRecord,
  ThirdPartyPermission,
  ThirdPartyPermissionRecord,
  ThirdPartyProvider,
} from '@dealpoint/types'
import intersection from 'lodash/intersection'
import uniq from 'lodash/uniq'

import { NodeId, NodePath, UserId } from './types'

export const createNodeId = ({
  path,
  provider,
}: Partial<Pick<IThirdPartyNode, 'path' | 'provider'>>) => `${provider}--${path}`

export const GLOBAL_NODE_ID = createNodeId({ path: '/', provider: 'all' })

export const isAncestor = (nodeId: NodeId, parentNodeId: NodeId) =>
  nodeId.startsWith(parentNodeId) && nodeId !== parentNodeId

export const isGlobalNode = ({ path, provider }: Pick<IThirdPartyNode, 'path' | 'provider'>) =>
  path === '/' && provider === 'all'

export const isRootNode = ({ path, provider }: Pick<IThirdPartyNode, 'path' | 'provider'>) =>
  path === '/' && provider !== 'all'

export type PermissionGuardHandler<T = ThirdPartyPermissionRecord> = (
  record: T,
  parentUserIds: UserId[],
  globalPermissions: ThirdPartyGlobalPermissionRecord
) => T

export const permissionSetGuards: Record<ThirdPartyPermission, PermissionGuardHandler> = {
  manageHidden: (record, parentUserIds) => {
    return parentUserIds?.length
      ? {
          ...record,
          manageHidden: uniq((record.manageHidden ?? []).concat(parentUserIds)),
        }
      : { ...record, manageHidden: parentUserIds }
  },
  visible: (record, parentUserIds, globalPermissions) => {
    const { visible } = record
    const { hidden } = globalPermissions ?? {}

    const guard = (userIds: string[]) =>
      hidden?.length ? userIds.filter((userId) => !hidden.includes(userId)) : userIds

    if (!visible) {
      return { ...record, visible: null }
    }

    if (!visible.length || !parentUserIds) {
      return {
        ...record,
        visible: guard(visible || []),
      }
    }

    if (parentUserIds && !parentUserIds?.length) {
      return { ...record, visible: [] }
    }

    return {
      ...record,
      visible: guard(intersection(visible, parentUserIds)),
    }
  },
}

export const permissionGetGuards: Record<ThirdPartyPermission, PermissionGuardHandler> = {
  manageHidden: permissionSetGuards.manageHidden,
  visible: (record, parentUserIds = [], globalPermissions) => {
    const { visible } = record
    const { hidden } = globalPermissions ?? {}

    const guard = (userIds) =>
      hidden?.length ? userIds.filter((userId) => !hidden.includes(userId)) : userIds

    if (visible) {
      return parentUserIds
        ? { ...record, visible: guard(intersection(visible, parentUserIds)) }
        : { ...record, visible: guard(visible) }
    }

    if (parentUserIds) {
      return { ...record, visible: guard(parentUserIds) }
    }

    return { ...record, visible: null }
  },
}

export const globalPermissionSetGuards: Record<ThirdPartyGlobalPermission, PermissionGuardHandler> =
  {
    manageHidden: permissionSetGuards.manageHidden,
    hidden: (record, _, globalPermissions) => {
      const { hidden } = globalPermissions ?? {}

      return {
        ...record,
        visible: hidden?.length
          ? record.visible?.filter((userId) => !hidden.includes(userId))
          : record.visible,
      }
    },
  }

export const getNodeIdParts = (id: NodeId): { path: NodePath; provider: ThirdPartyProvider } => {
  const parts = id.split('--')
  return { path: parts[1], provider: parts[0] as ThirdPartyProvider }
}

export function createGuardPermissions<
  R = ThirdPartyPermissionRecord,
  P = ThirdPartyPermissionRecord
>(
  guards: Record<string, PermissionGuardHandler<R>>,
  globalPermissions: ThirdPartyGlobalPermissionRecord
) {
  return (record: R, parentRecord: P) => {
    return Object.entries(parentRecord).reduce(
      (acc, [type, userIds]) =>
        guards[type] ? guards[type](acc, userIds, globalPermissions) : acc,
      record
    )
  }
}

export const getParentPermissions = (
  id: NodeId,
  byNodeId: Record<NodeId, ThirdPartyPermissionRecord>,
  globalPermissions: ThirdPartyGlobalPermissionRecord
) =>
  Object.entries(byNodeId).reduce<ThirdPartyPermissionRecord>(
    (acc, [nodeId, record]) => {
      if (isAncestor(id, nodeId)) {
        return Object.entries(record).reduce(
          (acc, [type, userIds]) => permissionGetGuards[type](acc, userIds, globalPermissions),
          acc
        )
      }

      return acc
    },
    { manageHidden: null, visible: null } as ThirdPartyPermissionRecord
  )

type GlobalPermissionGuard = (
  permissionedUserIds: string[],
  globalPermissions: ThirdPartyGlobalPermissionRecord
) => string[]

export const globalPermissionGuards: Record<ThirdPartyPermission, GlobalPermissionGuard> = {
  manageHidden: (userIds, globalPermissions) => {
    const globalUserIds = globalPermissions.manageHidden
    return globalUserIds ?? userIds
  },
  visible: (userIds, globalPermissions) => {
    const hiddenUserIds = globalPermissions?.hidden
    return hiddenUserIds ? userIds?.filter((id) => !hiddenUserIds.includes(id)) : userIds
  },
}
